import { useRef, useState, useMemo } from 'react';
import { getActionFromKeyboardEvent, navigateOptions } from './keyboard.js';
import { setIsEqual } from '../util.js';
import { useUniqueId } from '../../util/uniqueId.js';

/* The default implementation for the `searchFn` prop. */
function defaultSearchFn(options, search) {
  return options.filter(option => option.label.toUpperCase().includes(search.trim().toUpperCase()));
}
function defaultMultiSummary(selectedOptions, formatMessage) {
  if (selectedOptions.length === 1) {
    return selectedOptions[0].label;
  }
  if (selectedOptions.length > 1) {
    return formatMessage('{count} selected', {
      count: selectedOptions.length
    });
  }
}
function areAllSelected(selectedValues, options) {
  if (selectedValues.length === 0) {
    return false;
  }
  const selectedValueSet = new Set(selectedValues);
  const allValues = new Set(options.map(option => option.value));
  return setIsEqual(selectedValueSet, allValues);
}
function useSelectDropdown(props) {
  const {
    options,
    extraValues,
    searchFn = defaultSearchFn,
    searchMode = 'local',
    filterOptions = () => true,
    onOpen,
    onClose,
    onSearch,
    onOptionSelect,
    onActiveOptionCollapse,
    onActiveOptionExpand,
    UNSAFE_initialOpen
  } = props;
  const uid = useUniqueId();
  const searchRef = useRef(null);
  const [open, setOpen] = useState(UNSAFE_initialOpen ?? false);
  const [search, setSearch] = useState('');

  /* The value of the active option (the option focused via keyboard) */
  const [activeValue, setActiveValue] = useState(null);
  const filteredOptions = useMemo(() => searchMode === 'local' ? searchFn(options, search) : options, [searchMode, options, search, searchFn]).filter(filterOptions);
  const activeOption = filteredOptions.find(option => option.value === activeValue);
  function openPopover() {
    setOpen(true);
    onOpen?.();
  }
  function closePopover() {
    let clearSearch = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    setOpen(false);
    setActiveValue(null);
    if (clearSearch) {
      setSearch('');
    }
    onClose?.();
  }
  function handleDropdownClick(event) {
    if (open) {
      // Clicking on the filter text box should not collapse the popover.
      if (event.target !== searchRef.current) {
        closePopover(false);
      }
    } else {
      openPopover();
      searchRef.current?.focus();
    }
  }
  function handleSearchInput(event) {
    const value = event.currentTarget.value;
    setSearch(value);
    onSearch?.(value);
    if (!open) {
      openPopover();
    }
  }
  function handleSearchKeyDown(event) {
    const action = getActionFromKeyboardEvent(event, open);
    switch (action) {
      case 'open':
        openPopover();
        event.preventDefault();
        break;
      case 'close':
        closePopover();
        event.preventDefault();
        break;
      case 'select':
        if (activeValue != null) {
          onOptionSelect(activeValue);
        } else {
          closePopover();
        }
        event.preventDefault();
        break;
      case 'collapse':
        if (onActiveOptionCollapse) {
          onActiveOptionCollapse();
          event.preventDefault();
        }
        break;
      case 'expand':
        if (onActiveOptionExpand) {
          onActiveOptionExpand();
          event.preventDefault();
        }
        break;
      case 'previous':
      case 'next':
      case 'pageup':
      case 'pagedown':
        if (filteredOptions.length > 0) {
          setActiveValue(navigateOptions({
            action,
            options: filteredOptions,
            extraValues,
            activeValue
          }));
        }
        event.preventDefault();
        break;
    }
  }
  return {
    uid,
    searchRef,
    open,
    search,
    activeValue,
    setActiveValue,
    filteredOptions,
    activeOption,
    closePopover,
    onDropdownClick: handleDropdownClick,
    onSearchInput: handleSearchInput,
    onSearchKeyDown: handleSearchKeyDown
  };
}

export { areAllSelected, defaultMultiSummary, defaultSearchFn, useSelectDropdown };
